<template>
  <NumeraiModal
    ref="modal"
    :dialog="dialog"
    :full-width="fullWidth"
    :button="button"
    button-text="Manage Stake"
    :width="600"
    :show-footer="false"
    :color="isDarkMode ? 'var(--background-color)' : 'white'"
    :button-class="buttonClass"
    @open="open"
    @close="close"
  >
    <!-- Header -->
    <template v-slot:header>
      <v-container class="header">
        <h2 class="title">
          Manage Stake
        </h2>
        <div class="closeButton" @click="$refs.modal.close()">&times;</div>
      </v-container>
    </template>

    <v-container>
      <v-select
        :items="modelSelectItems"
        :value="modelName"
        label="Select a model"
        class="pt-0 pb-0"
        @change="onModelChange"
      >
      </v-select>
    </v-container>

    <template v-if="selectedModel !== null">
      <!-- Summary -->
      <v-container>
        <div class="mb-1">
          Model Name
          <span class="float-right">{{ modelName }}</span>
        </div>
        <div class="mb-1">
          Stake Value
          <span class="float-right"> {{ stakeValue }} NMR</span>
        </div>
        <div class="mb-1">
          Payout Mode
          <span class="float-right">{{ selectedPayoutMode }}</span>
        </div>
        <div>
          Score Multipliers
          <span class="float-right">{{ scoreMultipliersDescription }}</span>
        </div>
      </v-container>

      <v-container>
        <!-- Tabs -->
        <v-container class="tabs pb-0">
          <v-row>
            <v-col
              :class="{ selectedTab: selectedTab == 'stake' }"
              @click="selectTab('stake')"
            >
              Stake
            </v-col>
            <v-col
              :class="{ selectedTab: selectedTab == 'release' }"
              @click="selectTab('release')"
            >
              Release
            </v-col>
            <v-col
              :class="{ selectedTab: selectedTab == 'advanced' }"
              @click="selectTab('advanced')"
            >
              Advanced
            </v-col>
          </v-row>
        </v-container>

        <!-- Stake Form -->
        <v-container
          v-if="selectedTab == 'stake'"
          class="tabContent roundedBottom roundedTopRight"
        >
          <p>
            <span class="label">
              Stake Amount
              <NumeraiTooltip
                v-if="!pendingDrain"
                text="Increase your stake by the submission deadline
                for it apply to the current round."
              />
              <NumeraiTooltip
                v-else
                text="Staking is disabled while your stake is draining."
              />
            </span>
          </p>
          <v-text-field
            v-model="stakeForm.amount"
            class="inputWithDetails"
            solo
            flat
            label="Amount"
            suffix="NMR"
            type="number"
            step="any"
            :rules="stakeAmountRules"
            :disabled="pendingDrain"
            @keydown="onAmountKeydown($event)"
          />
          <span class="formHelpers">
            Available:
            {{ walletBalance }} NMR
            <span v-if="!pendingDrain"
              >(<span
                class="underline-btn"
                @click="stakeForm.amount = walletBalance"
                >max</span
              >)</span
            >&nbsp;<NumeraiTooltip
              text="Includes wallet balance and stake credits."
            />
          </span>
          <button
            :disabled="!enableStakeConfirmButton"
            class="numerai-button full-width mb-0 mt-3"
            @click="confirmIncrease"
          >
            Confirm
          </button>
        </v-container>

        <!-- Release Form -->
        <v-container
          v-if="selectedTab == 'release' && !pendingRelease"
          class="tabContent roundedBottom roundedTopLeft roundedTopRight"
        >
          <p>
            <span v-if="!releaseForm.drain" class="label">
              Release Amount
              <NumeraiTooltip
                text="While pending release, this amount will not count
                towards your model's NMR at-risk for upcoming rounds."
              />
            </span>
            <span v-else class="label">Drain Stake</span>
            <span class="float-right"
              >Release Date: {{ releaseDate }}
              <NumeraiTooltip
                text="Staked NMR can only be released at the resolution of
                then latest round where your model had any NMR at-risk."
                text2="If your model has no NMR at-risk in any ongoing round,
                then the staked NMR will be released after one day."
            /></span>
          </p>
          <v-text-field
            v-if="!releaseForm.drain"
            v-model="releaseForm.amount"
            class="inputWithDetails"
            solo
            flat
            label="Amount"
            suffix="NMR"
            type="number"
            step="any"
            :rules="releaseAmountRules"
            @keydown="onAmountKeydown($event)"
          />
          <p v-else>
            Release 100% of your model's stake value on the release date,
            including any earnings compounded in the meantime. While draining,
            your model's at-risk stake value will be 0 and you will not be able
            to increase your stake.
          </p>
          <span v-if="!releaseForm.drain" class="formHelpers">
            Available:
            {{ stakeValue }} NMR (<span
              class="underline-btn"
              @click="releaseForm.amount = stakeValue"
              >max</span
            >)
          </span>

          <span class="float-right">
            <v-checkbox
              v-model="releaseForm.drain"
              class="ma-0 pa-0"
              :ripple="false"
              hide-details
            >
              <template v-slot:label>
                <span class="formHelpers">
                  Drain
                </span>
              </template>
            </v-checkbox>
          </span>
          <button
            :disabled="!enableReleaseConfirmButton"
            class="numerai-button full-width mb-0 mt-3"
            @click="confirmRelease"
          >
            Confirm
          </button>
        </v-container>

        <!-- Release Form (w/ Pending Release)-->
        <div v-if="selectedTab == 'release' && pendingRelease">
          <v-container class="tabContent roundedTopLeft roundedTopRight">
            <p>
              <span v-if="!pendingDrain" class="label">
                Pending Release
                <NumeraiTooltip
                  text="While pending release, this amount will not count
                  towards your model's NMR at-risk for upcoming rounds."
                />
              </span>
              <span v-else class="label">Draining Stake</span>
              <span class="float-right"
                >Release Date: {{ pendingReleaseDueDate }}
              </span>
            </p>
            <v-text-field
              v-if="!pendingRelease.drain"
              v-model="pendingRelease.requestedAmount"
              hide-details
              solo
              flat
              label="Amount"
              suffix="NMR"
              type="number"
              step="any"
              disabled
            />
            <p v-else>
              Release 100% of your model's stake value on the release date,
              including any earnings compounded in the meantime. While draining,
              your model's at-risk stake value will be 0 and you will not be
              able to increase your stake.
            </p>
          </v-container>
          <v-container class="tabAttachment roundedBottom">
            <div v-if="!releaseForm.showCancelConfirm">
              <div>
                You may only have one pending release at a time. Cancel this
                pending release to create a new one.
              </div>
              <div class="text-right">
                <span
                  class="underline-btn"
                  @click="releaseForm.showCancelConfirm = true"
                  >Cancel</span
                >
              </div>
            </div>
            <div v-else>
              <div>
                Are you sure you want to cancel this pending release and reset
                the release date? This action can not be undone.
              </div>
              <div class="text-right">
                <span class="underline-btn" @click="cancelPendingStakeRelease"
                  >Yes</span
                >
                &nbsp;
                <span
                  class="underline-btn"
                  @click="releaseForm.showCancelConfirm = false"
                  >No</span
                >
              </div>
            </div>
          </v-container>
        </div>

        <!-- Advanced Form -->
        <v-container
          v-if="selectedTab == 'advanced'"
          class="tabContent roundedBottom roundedTopLeft"
        >
          <p class="label">
            Payout Mode
            <NumeraiTooltip
              text="Choose where to send your model's earnings for upcoming
              round resolutions."
              text2="Save your choice by the round resolution time on Friday
              15:30 UTC for it to apply to the latest resolving round."
            />
          </p>
          <v-row class="pb-3">
            <v-col cols="6" class="pt-0">
              <div
                class="numerai-paper outlineBtn pa-2"
                :class="{
                  selectedOutlineBtn: advancedForm.payoutMode == 'Compound',
                }"
                @click="advancedForm.payoutMode = 'Compound'"
              >
                <h4>Compound</h4>
                <span>Keep all earnings in stake <br />(default setting)</span>
              </div>
            </v-col>
            <v-spacer />
            <v-col cols="6" class="pt-0">
              <div
                class="numerai-paper outlineBtn pa-2"
                :class="{
                  selectedOutlineBtn: advancedForm.payoutMode == 'Wallet',
                }"
                @click="advancedForm.payoutMode = 'Wallet'"
              >
                <h4>Take Profit</h4>
                <span
                  >Remove all earnings <br />
                  ≥1 NMR to wallet</span
                >
              </div>
            </v-col>
          </v-row>
          <button
            :disabled="!enableAdvancedSaveButton"
            class="numerai-button full-width mb-0 mt-3"
            @click="saveAdvanced"
          >
            Save
          </button>
        </v-container>
      </v-container>
    </template>

    <!-- Legal Disclaimer -->
    <v-container class="legal">
      Staking is not an offer by Numerai to participate in an investment
      contract, a security, a swap based on the return of any financial assets,
      an interest in Numerai’s hedge fund, or in Numerai itself or any fees we
      earn. Please read our Terms of Service for further information.
    </v-container>
  </NumeraiModal>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapState, mapGetters } from 'vuex'
import NumeraiModal from '@/components/shared/NumeraiModal'
import NumeraiTooltip from '@/components/shared/NumeraiTooltip'
import { getTournamentNumber, formatStakeType } from '@/lib/utils'
import moment from 'moment'
import BigNumber from 'bignumber.js'

export default {
  components: {
    NumeraiModal,
    NumeraiTooltip,
  },
  props: {
    model: { type: Object, default: null },
    models: { type: Array, default: null },
    fill: { type: Boolean, default: true },
    fullWidth: { type: Boolean, default: true },
    activeStakes: { type: Array, default: null },
    isHyperlink: { type: Boolean, default: false },
    buttonClass: { type: String, default: null },
    onClose: { type: Function, default: null },
    dialog: { type: Boolean, default: false },
    button: { type: Boolean, default: true },
    onStake: { type: Function, default: null },
  },
  data() {
    return {
      roundInfo: null,
      selectedTab: 'stake',
      stakeForm: {
        amount: '',
        defaultAmount: '',
      },
      releaseForm: {
        amount: '',
        defaultAmount: '',
        drain: false,
        showCancelConfirm: false,
      },
      advancedForm: {
        corrMultiplier: undefined,
        tcMultiplier: undefined,
        payoutMode: undefined,
      },
      inProgress: false,
      defaultModel: null,
      selectedModel: null,
    }
  },
  computed: {
    ...mapState(['accountInfo', 'latestRoundInfo']),
    ...mapGetters(['isDarkMode']),
    modelSelectItems() {
      if (!this.models) {
        return []
      }
      return this.models.map(model => model.name && model.name.toUpperCase())
    },
    walletBalance() {
      return BigNumber(this.accountInfo.availableNmr).plus(
        this.accountInfo.availableStakeCredit,
      )
    },
    modelName() {
      return (
        this.selectedModel &&
        this.selectedModel.name &&
        this.selectedModel.name.toUpperCase()
      )
    },
    releaseDate() {
      return moment(new Date(this.selectedModel.earliestReleaseDate))
        .utc()
        .format('YYYY-MM-DD')
    },
    pendingReleaseDueDate() {
      return moment(new Date(this.pendingRelease.dueDate))
        .utc()
        .format('YYYY-MM-DD')
    },
    stakeValue() {
      return parseFloat(_.get(this.selectedModel, 'v2Stake.stakeValue', 0) || 0)
    },
    corrMultipliers() {
      let multipliers = {
        min: null,
        max: null,
        default: null,
        options: [],
      }
      if (this.latestRoundInfo !== undefined) {
        multipliers.min = parseFloat(this.latestRoundInfo.minCorrMultiplier)
        multipliers.max = parseFloat(this.latestRoundInfo.maxCorrMultiplier)
        multipliers.default = parseFloat(
          this.latestRoundInfo.defaultCorrMultiplier,
        )
        var options = _.range(multipliers.min, multipliers.max + 1, 0.5)
        multipliers.options = options.slice(0, -1)
      }
      return multipliers
    },
    mmcMultipliers() {
      let multipliers = {
        min: null,
        max: null,
        default: null,
        options: [],
      }
      if (this.latestRoundInfo !== undefined) {
        multipliers.min = parseFloat(this.latestRoundInfo.minMmcMultiplier)
        multipliers.max = parseFloat(this.latestRoundInfo.maxMmcMultiplier)
        multipliers.default = parseFloat(
          this.latestRoundInfo.defaultMmcMultiplier,
        )
        var options = _.range(multipliers.min, multipliers.max + 1, 0.5)
        multipliers.options = options.slice(0, -1)
      }
      return multipliers
    },
    tcMultipliers() {
      let multipliers = {
        min: null,
        max: null,
        default: null,
        options: [],
      }
      if (this.latestRoundInfo !== undefined) {
        multipliers.min = parseFloat(this.latestRoundInfo.minTcMultiplier)
        multipliers.max = parseFloat(this.latestRoundInfo.maxTcMultiplier)
        multipliers.default = parseFloat(
          this.latestRoundInfo.defaultTcMultiplier,
        )
        var options = _.range(multipliers.min, multipliers.max + 1, 0.5)
        multipliers.options = options.slice(0, -1)
      }
      return multipliers
    },
    selectedCorrMultiplier() {
      return _.get(
        this.selectedModel,
        'currentPayoutSelection.corrMultiplier',
        this.corrMultipliers.default,
      )
    },
    selectedMmcMultiplier() {
      return _.get(
        this.selectedModel,
        'currentPayoutSelection.mmcMultiplier',
        this.mmcMultipliers.default,
      )
    },
    selectedTcMultiplier() {
      return _.get(
        this.selectedModel,
        'currentPayoutSelection.tcMultiplier',
        this.tcMultipliers.default,
      )
    },
    selectedPayoutMode() {
      return _.get(
        this.selectedModel,
        'currentPayoutSelection.takeProfit',
        false,
      )
        ? 'Wallet'
        : 'Compound'
    },
    stakeAmountRules() {
      return [
        amount =>
          amount === '' || amount > 0 || 'Stake amount must be greater than 0',
        amount =>
          amount === '' ||
          this.lessThanOrEqualTo(amount, this.walletBalance) ||
          `Insufficient available balance`,
      ]
    },
    scoreMultipliersDescription() {
      return formatStakeType(
        this.selectedCorrMultiplier,
        this.selectedMmcMultiplier,
        this.selectedTcMultiplier,
      )
    },
    releaseAmountRules() {
      return [
        amount =>
          amount === '' ||
          amount > 0 ||
          'Release amount must be greater than 0',
        amount =>
          amount === '' ||
          this.lessThanOrEqualTo(amount, this.stakeValue) ||
          `Insufficient available balance`,
      ]
    },
    pendingDrain() {
      return _.get(
        this.selectedModel,
        'v2Stake.pendingV2ChangeStakeRequest.drain',
        false,
      )
    },
    pendingRelease() {
      return _.get(
        this.selectedModel,
        'v2Stake.pendingV2ChangeStakeRequest',
        null,
      )
    },
    enableStakeConfirmButton() {
      const changed = this.stakeForm.amount != this.stakeForm.defaultAmount
      const valid = !!this.stakeAmountRules.every(rule => {
        if (rule(this.stakeForm.amount) === true) {
          return rule
        } else {
          return false
        }
      })
      return changed && valid && !this.inProgress
    },
    enableReleaseConfirmButton() {
      const changed = this.releaseForm.amount != this.releaseForm.defaultAmount
      const valid = !!this.releaseAmountRules.every(rule => {
        if (rule(this.releaseForm.amount) === true) {
          return rule
        } else {
          return false
        }
      })
      const drain = this.releaseForm.drain
      return ((changed && valid) || drain) && !this.inProgress
    },
    enableAdvancedSaveButton() {
      const payoutModeChanged =
        this.advancedForm.payoutMode !== this.selectedPayoutMode
      const corrMultiplierChanged =
        this.advancedForm.corrMultiplier !== this.selectedCorrMultiplier
      const tcMultiplierChanged =
        this.advancedForm.tcMultiplier !== this.selectedTcMultiplier
      return (
        (payoutModeChanged || corrMultiplierChanged || tcMultiplierChanged) &&
        !this.inProgress
      )
    },
  },
  watch: {
    model: function(newState, oldState) {
      if (JSON.stringify(newState) !== JSON.stringify(oldState)) {
        this.defaultModel = newState
        this.selectedModel = newState
      }
    },
  },
  created() {
    this.$store.dispatch('fetchLatestRoundInfo', getTournamentNumber())
  },
  mounted() {
    this.open()
    this.resetForms()

    // Store model into local state so we can support changing the selected
    // model from within this modal
    if (this.model) {
      this.defaultModel = this.model
      this.selectedModel = this.model
    }

    if (this.pendingRelease) {
      this.selectTab('release')
    }
  },
  methods: {
    ...mapActions(['showSnackBar', 'fetchAccountInfo']),
    async open() {
      this.resetForms()
    },
    close() {
      const fullReset = true
      this.resetForms(fullReset)
      this.onClose && this.onClose()
    },
    resetForms(fullReset) {
      if (fullReset) {
        this.selectedModel = this.defaultModel
      }
      this.stakeForm.amount = this.stakeForm.defaultAmount
      this.releaseForm.amount = this.releaseForm.defaultAmount
      this.releaseForm.drain = false
      this.releaseForm.showCancelConfirm = false
      this.advancedForm.payoutMode = this.selectedPayoutMode
      this.advancedForm.corrMultiplier = this.selectedCorrMultiplier
      this.advancedForm.tcMultiplier = this.selectedTcMultiplier
    },
    onModelChange(modelName) {
      this.selectedModel = this.models.filter(
        model => (model.name && model.name.toUpperCase()) === modelName,
      )[0]
    },
    selectTab(name) {
      this.selectedTab = name
      this.resetForms()
    },
    onAmountKeydown(e) {
      // prevent `-` and `e` characters allowed by vuetify number input
      if (e.key === '-' || e.key === 'e') {
        e.preventDefault()
      }
    },
    async confirmIncrease() {
      try {
        this.inProgress = true
        await this.$client.increaseStake({
          amount: this.stakeForm.amount.toString(),
          modelId: this.selectedModel.id,
        })
        await this.fetchAccountInfo()
        this.showSnackBar({ msg: 'Stake increase applied 🎉' })
        this.onStake && this.onStake()
        this.inProgress = false
        this.$refs.modal.close()
      } catch (e) {
        this.showSnackBar({ msg: e.message })
        this.inProgress = false
      }
    },
    async confirmRelease() {
      try {
        this.inProgress = true
        if (this.releaseForm.drain == true) {
          this.releaseForm.amount = this.stakeValue
        }
        await this.$client.releaseStake({
          amount: this.releaseForm.amount.toString(),
          modelId: this.selectedModel.id,
          drain: this.releaseForm.drain,
        })
        await this.fetchAccountInfo()
        this.inProgress = false
        this.showSnackBar({ msg: 'Stake release scheduled 🕐️' })
        this.$refs.modal.close()
      } catch (e) {
        this.showSnackBar({ msg: e.message })
        this.inProgress = false
      }
    },
    async cancelPendingStakeRelease() {
      try {
        this.inProgress = true
        await this.$client.cancelPendingStakeRelease(this.selectedModel.id)
        await this.fetchAccountInfo()
        this.inProgress = false
        this.showSnackBar({ msg: 'Stake release canceled ❌️' })
        this.$refs.modal.close()
      } catch (e) {
        this.showSnackBar({ msg: e.message })
        this.inProgress = false
      }
    },
    async saveAdvanced() {
      try {
        this.inProgress = true
        var takeProfit = this.advancedForm.payoutMode === 'Wallet'
        await this.$client.changePayoutSelection({
          takeProfit: takeProfit,
          tournament: getTournamentNumber(),
          modelId: this.selectedModel.id,
        })
        await this.fetchAccountInfo()
        this.inProgress = false
        this.showSnackBar({ msg: 'Advanced payout options saved 🎉' })
        this.$refs.modal.close()
      } catch (e) {
        this.showSnackBar({ msg: e.message })
        this.inProgress = false
      }
    },
    lessThanOrEqualTo(amountA, amountB) {
      const comparison = BigNumber(amountA).comparedTo(amountB)
      return comparison === -1 || comparison === 0
    },
  },
}
</script>

<style scoped>
.header {
  display: flex;
  justify-content: space-between;
}
.closeButton {
  font-size: 40px;
  font-weight: 100;
  cursor: pointer;
  font-family: var(--brand-font);
  color: var(--color-subtle);
  line-height: 1;
}
.closeButton:hover {
  color: var(--color-primary);
}

.tabs {
  text-align: center;
  cursor: pointer;
  color: var(--color-secondary);
  border-radius: 3px;
  font-size: 16px;
  font-weight: bold;
}
.selectedTab {
  background-color: var(--paper-background-highlight1);
  border-radius: 3px 3px 0px 0px;
}
.tabContent {
  background-color: var(--paper-background-highlight1);
  padding: 15px;
}
.roundedBottom {
  border-radius: 0 0 3px 3px;
}
.roundedTopLeft {
  border-top-left-radius: 3px;
}
.roundedTopRight {
  border-top-right-radius: 3px;
}
.tabAttachment {
  background-color: var(--paper-background-highlight2);
}

.formHelpers {
  font-size: 0.9rem;
  line-height: 24px;
}
.label {
  font-size: 16px;
  font-weight: bold;
}
.inputWithDetails {
  min-height: 79px;
}
.underline-btn {
  text-decoration: underline;
  cursor: pointer;
}
.outlineBtn {
  cursor: pointer;
  text-align: center;
  border-radius: 25px;
  color: var(--color-subtle);
  background-color: var(--paper-background-secondary-color);
  box-shadow: none;
}
.selectedOutlineBtn {
  color: var(--color-secondary);
  background-color: var(--paper-background-color);
  box-shadow: 0px 4px 18px rgba(0, 0, 0, 0.04);
}
.maths {
  background-color: var(--paper-background-highlight1);
  font-size: 12px;
  font-weight: 400;
  font-family: monospace;
  text-align: center;
  margin: 0 auto;
}

.legal {
  font-weight: 400;
  font-size: 11px;
  font-style: italic;
  line-height: 1.2;
  margin-top: 10px;
}
</style>
